import React, { useEffect, useRef, useState } from "react";
import { api, API } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Trash2, GripVertical, X, Upload, Sparkles } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import ImagePicker from "@/components/ImagePicker";

const emptyQ = { text: "", type: "multiple_choice", options: [{ label: "", tags: [], image_path: null }], order: 0, image_path: null };

function uploadImageReq(file) {
  const fd = new FormData();
  fd.append("file", file);
  return api.post("/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
}

function ImageBox({ path, onUpload, testid }) {
  return (
    <ImagePicker path={path} onChange={onUpload} size="sm" testid={testid} />
  );
}

export default function Questions() {
  const [items, setItems] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [form, setForm] = useState(emptyQ);
  const [busy, setBusy] = useState(false);
  const csvRef = useRef(null);
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [suggestForm, setSuggestForm] = useState({ website: "", brand_about: "", count: 5 });
  const [suggesting, setSuggesting] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [picked, setPicked] = useState({}); // index -> bool
  const [importing, setImporting] = useState(false);

  const load = async () => {
    const { data } = await api.get("/questions");
    setItems(data);
    if (data.length && !selectedId) selectFromList(data[0]);
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  // Generate a stable React key for an option (not persisted server-side)
  const optKey = () =>
    (typeof crypto !== "undefined" && crypto.randomUUID)
      ? crypto.randomUUID()
      : `o_${Date.now()}_${Math.random()}`;

  const selectFromList = (q) => {
    setSelectedId(q.id);
    setForm({
      text: q.text || "",
      type: q.type || "multiple_choice",
      options: q.options?.length
        ? q.options.map((o) => ({ _key: optKey(), label: o.label || "", tags: o.tags || [], image_path: o.image_path || null }))
        : [{ _key: optKey(), label: "", tags: [], image_path: null }],
      order: q.order || 0,
      image_path: q.image_path || null,
    });
  };

  const addNew = async () => {
    const { data } = await api.post("/questions", {
      text: "New question",
      type: "multiple_choice",
      options: [{ label: "Option A", tags: [], image_path: null }, { label: "Option B", tags: [], image_path: null }],
      order: items.length + 1,
      image_path: null,
    });
    setItems([...items, data]);
    selectFromList(data);
  };

  const save = async () => {
    if (!selectedId) return;
    setBusy(true);
    try {
      const payload = {
        ...form,
        options: form.options
          .filter((o) => o.label.trim())
          // strip internal-only React key before sending to backend
          .map(({ _key, ...rest }) => rest), // eslint-disable-line no-unused-vars
      };
      const { data } = await api.put(`/questions/${selectedId}`, payload);
      setItems((prev) => prev.map((q) => (q.id === selectedId ? data : q)));
      toast.success("Question saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    }
    setBusy(false);
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this question?")) return;
    try {
      await api.delete(`/questions/${id}`);
      setItems((prev) => {
        const next = prev.filter((q) => q.id !== id);
        if (selectedId === id) {
          if (next.length) selectFromList(next[0]);
          else { setSelectedId(null); setForm(emptyQ); }
        }
        return next;
      });
      toast.success("Question deleted");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const runSuggest = async () => {
    setSuggesting(true);
    try {
      const { data } = await api.post("/questions/suggest", suggestForm);
      const qs = data.questions || [];
      setSuggestions(qs);
      const init = {};
      qs.forEach((_, i) => { init[i] = true; });
      setPicked(init);
      if (!qs.length) toast.error("No suggestions returned. Add more catalog detail and retry.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "AI suggest failed");
    }
    setSuggesting(false);
  };

  const importPicked = async () => {
    const chosen = suggestions.filter((_, i) => picked[i]);
    if (!chosen.length) {
      toast.error("Pick at least one question");
      return;
    }
    setImporting(true);
    try {
      await api.post("/questions/bulk", chosen.map((q, idx) => ({
        text: q.text,
        type: "multiple_choice",
        options: q.options,
        order: items.length + idx + 1,
        image_path: null,
      })));
      toast.success(`${chosen.length} questions added`);
      setSuggestOpen(false);
      setSuggestions([]);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Import failed");
    }
    setImporting(false);
  };


  const uploadCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/questions/csv", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (data.inserted > 0) {
        toast.success(`${data.inserted} questions imported${data.skipped ? ` · ${data.skipped} skipped` : ""}`);
      } else {
        const detected = (data.detected_headers || []).join(", ") || "none";
        toast.error(`0 imported. Detected: [${detected}]. Need a "question" column + option_1, option_1_tags, option_2…`, { duration: 8000 });
      }
      await load();
    } catch {
      toast.error("CSV import failed");
    }
    e.target.value = "";
  };

  const updateOption = (i, key, value) => {
    const opts = [...form.options];
    opts[i] = { ...opts[i], [key]: value };
    setForm({ ...form, options: opts });
  };
  const addOption = () =>
    setForm({
      ...form,
      options: [
        ...form.options,
        { _key: optKey(), label: "", tags: [], image_path: null },
      ],
    });
  const removeOption = (i) => setForm({ ...form, options: form.options.filter((_, idx) => idx !== i) });

  return (
    <>
      <PageHeader
        eyebrow="Setup"
        title="Questions"
        description="Design 3–7 questions you'd ask a customer in person to find their perfect product. Optionally add visual cues to each option."
      >
        <input ref={csvRef} type="file" accept=".csv" hidden onChange={uploadCsv} data-testid="questions-csv-input" />
        <Button
          variant="outline"
          onClick={() => setSuggestOpen(true)}
          data-testid="suggest-questions-button"
          className="rounded-none border-[#0F1210] text-[#0F1210] hover:bg-[#0F1210] hover:text-white h-11 px-5"
        >
          <Sparkles className="mr-2 w-4 h-4" /> Suggest with AI
        </Button>
        <Button
          variant="outline"
          onClick={() => csvRef.current?.click()}
          data-testid="questions-csv-button"
          className="rounded-none border-[#e5e5e5] h-11 px-5"
        >
          <Upload className="mr-2 w-4 h-4" /> Import CSV
        </Button>
        <Button
          onClick={addNew}
          data-testid="add-question-button"
          className="rounded-none bg-[#0F1210] hover:bg-black text-white h-11 px-5"
        >
          <Plus className="mr-2 w-4 h-4" /> Add question
        </Button>
      </PageHeader>

      <div className="p-10 grid grid-cols-12 gap-6">
        {/* Left list */}
        <div className="col-span-12 lg:col-span-4">
          <div className="bg-white border border-[#e5e5e5]">
            <div className="px-4 py-3 border-b border-[#e5e5e5] label-overline">All questions ({items.length})</div>
            {items.length === 0 && (
              <div className="p-6 text-sm text-[#52525b]" data-testid="empty-questions">
                No questions yet. Add manually or import a CSV with columns:
                <span className="font-mono"> question, option_1, option_1_tags, option_2, option_2_tags, …</span>
              </div>
            )}
            {items.map((q, idx) => (
              <div
                key={q.id}
                className={`group flex items-center gap-2 px-3 py-3 border-b border-[#e5e5e5] last:border-b-0 transition-colors ${
                  selectedId === q.id ? "bg-[#fafafa]" : "hover:bg-[#fafafa]"
                }`}
              >
                <button
                  onClick={() => selectFromList(q)}
                  data-testid={`question-list-${q.id}`}
                  className="flex items-center gap-3 flex-1 text-left min-w-0"
                >
                  <GripVertical className="w-4 h-4 text-[#a1a1aa] shrink-0" />
                  <span className="font-mono text-xs text-[#52525b] w-6 shrink-0">{String(idx + 1).padStart(2, "0")}</span>
                  <span className="flex-1 truncate text-sm">{q.text || "Untitled"}</span>
                </button>
                <button
                  onClick={() => remove(q.id)}
                  data-testid={`delete-question-list-${q.id}`}
                  title="Delete question"
                  className="p-2 text-[#a1a1aa] hover:text-[#e63946] hover:bg-white border border-transparent hover:border-[#e5e5e5] transition-colors"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Right editor */}
        <div className="col-span-12 lg:col-span-8">
          {!selectedId ? (
            <div className="bg-white border border-[#e5e5e5] p-16 text-center text-sm text-[#52525b]">
              Select a question to edit, or add a new one.
            </div>
          ) : (
            <div className="bg-white border border-[#e5e5e5] p-8 space-y-6">
              <div className="flex items-center justify-between">
                <div className="label-overline">Edit question</div>
                <button
                  onClick={() => remove(selectedId)}
                  data-testid="delete-question-button"
                  className="text-[#e63946] flex items-center gap-2 text-sm"
                >
                  <Trash2 className="w-4 h-4" /> Delete
                </button>
              </div>

              <div>
                <Label className="label-overline">Question text</Label>
                <Textarea
                  rows={2}
                  value={form.text}
                  onChange={(e) => setForm({ ...form, text: e.target.value })}
                  placeholder="e.g. What occasion are you shopping for?"
                  className="mt-2 rounded-none border-[#e5e5e5]"
                  data-testid="question-text"
                />
              </div>

              <div>
                <Label className="label-overline">Question hero image (optional)</Label>
                <div className="mt-2">
                  <ImageBox
                    path={form.image_path}
                    onUpload={(p) => setForm({ ...form, image_path: p })}
                    testid="question-image-input"
                  />
                </div>
              </div>

              <div>
                <Label className="label-overline">Answer options</Label>
                <div className="mt-3 space-y-3">
                  {form.options.map((opt, i) => (
                    <div key={opt._key || `idx_${i}`} className="border border-[#e5e5e5] p-4 space-y-3">
                      <div className="flex items-center gap-2">
                        <Input
                          value={opt.label}
                          onChange={(e) => updateOption(i, "label", e.target.value)}
                          placeholder={`Option ${i + 1} label`}
                          className="rounded-none border-[#e5e5e5] flex-1"
                          data-testid={`option-label-${i}`}
                        />
                        <button
                          onClick={() => removeOption(i)}
                          className="p-2 hover:bg-[#fafafa] border border-[#e5e5e5]"
                          data-testid={`option-remove-${i}`}
                          title="Remove option"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      <Input
                        value={(opt.tags || []).join(", ")}
                        onChange={(e) =>
                          updateOption(i, "tags", e.target.value.split(",").map((t) => t.trim()).filter(Boolean))
                        }
                        placeholder="tags (comma-separated) — drive AI matching"
                        className="rounded-none border-[#e5e5e5]"
                        data-testid={`option-tags-${i}`}
                      />
                      <ImageBox
                        path={opt.image_path}
                        onUpload={(p) => updateOption(i, "image_path", p)}
                        testid={`option-image-${i}`}
                      />
                    </div>
                  ))}
                  <Button
                    variant="outline"
                    onClick={addOption}
                    className="rounded-none h-9 border-[#e5e5e5]"
                    data-testid="add-option-button"
                  >
                    <Plus className="mr-2 w-3.5 h-3.5" /> Add option
                  </Button>
                </div>
                <p className="mt-3 text-xs text-[#52525b]">
                  Tags weight the AI matching. Option images appear as visual tiles on the kiosk.
                </p>
              </div>

              <div className="pt-2 flex justify-end">
                <Button
                  onClick={save}
                  disabled={busy}
                  data-testid="save-question-button"
                  className="rounded-none bg-[#0F1210] hover:bg-black text-white h-11 px-5"
                >
                  {busy ? "Saving…" : "Save changes"}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* AI Suggest Dialog */}
      <Dialog open={suggestOpen} onOpenChange={(o) => { setSuggestOpen(o); if (!o) setSuggestions([]); }}>
        <DialogContent className="rounded-none border border-[#e5e5e5] max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-display font-bold tracking-tight text-2xl flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-[#0F1210]" /> Suggest questions with AI
            </DialogTitle>
            <DialogDescription>
              Tell us about your brand. We'll read your catalog and propose questions that map answers to your products.
            </DialogDescription>
          </DialogHeader>

          {suggestions.length === 0 && (
            <div className="space-y-4">
              <div>
                <Label className="label-overline">Brand website (optional)</Label>
                <Input
                  value={suggestForm.website}
                  onChange={(e) => setSuggestForm({ ...suggestForm, website: e.target.value })}
                  placeholder="https://yourbrand.com"
                  className="mt-2 rounded-none border-[#e5e5e5]"
                  data-testid="suggest-website"
                />
              </div>
              <div>
                <Label className="label-overline">About your brand · style · target customer</Label>
                <Textarea
                  rows={4}
                  value={suggestForm.brand_about}
                  onChange={(e) => setSuggestForm({ ...suggestForm, brand_about: e.target.value })}
                  placeholder="e.g. We're a Tokyo-inspired streetwear label for urban runners. We care about clean silhouettes, performance fabrics, and limited colorways."
                  className="mt-2 rounded-none border-[#e5e5e5]"
                  data-testid="suggest-about"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="label-overline">How many questions?</Label>
                  <Input
                    type="number"
                    min={3}
                    max={8}
                    value={suggestForm.count}
                    onChange={(e) => setSuggestForm({ ...suggestForm, count: Number(e.target.value) || 5 })}
                    className="mt-2 rounded-none border-[#e5e5e5]"
                    data-testid="suggest-count"
                  />
                </div>
              </div>
              <p className="text-xs text-[#a1a1aa]">
                Powered by Claude Sonnet 4.5. Your catalog tags + brand context are used to generate option tags that drive the recommender.
              </p>
            </div>
          )}

          {suggestions.length > 0 && (
            <div className="max-h-[55vh] overflow-y-auto space-y-3 pr-2">
              <div className="label-overline text-[#52525b]">{suggestions.length} suggestions · tick the ones you want</div>
              {suggestions.map((q, i) => (
                <label
                  key={i}
                  className={`block border p-4 cursor-pointer transition-colors ${
                    picked[i] ? "border-[#0F1210] bg-[#fafafa]" : "border-[#e5e5e5]"
                  }`}
                  data-testid={`suggestion-${i}`}
                >
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      checked={!!picked[i]}
                      onChange={() => setPicked({ ...picked, [i]: !picked[i] })}
                      className="mt-1.5"
                      data-testid={`suggestion-check-${i}`}
                    />
                    <div className="flex-1">
                      <div className="font-medium text-sm">{q.text}</div>
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {q.options.map((o, j) => (
                          <span key={j} className="text-xs border border-[#e5e5e5] px-2 py-1 bg-white">
                            {o.label}
                            {o.tags?.length ? (
                              <span className="ml-1 text-[10px] text-[#a1a1aa] font-mono">
                                · {o.tags.join("·")}
                              </span>
                            ) : null}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </label>
              ))}
            </div>
          )}

          <DialogFooter>
            {suggestions.length > 0 && (
              <Button
                variant="outline"
                onClick={() => setSuggestions([])}
                className="rounded-none border-[#e5e5e5] h-11 px-5"
              >
                ← Regenerate
              </Button>
            )}
            <Button variant="outline" onClick={() => setSuggestOpen(false)} className="rounded-none border-[#e5e5e5] h-11 px-5">
              Cancel
            </Button>
            {suggestions.length === 0 ? (
              <Button
                onClick={runSuggest}
                disabled={suggesting}
                data-testid="run-suggest-button"
                className="rounded-none bg-[#0F1210] hover:bg-black text-white h-11 px-5"
              >
                {suggesting ? "Generating…" : "Generate suggestions"}
              </Button>
            ) : (
              <Button
                onClick={importPicked}
                disabled={importing}
                data-testid="import-suggestions-button"
                className="rounded-none bg-[#0F1210] hover:bg-black text-white h-11 px-5"
              >
                {importing ? "Adding…" : `Add ${Object.values(picked).filter(Boolean).length} to my questions`}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
